import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Box, Typography, Button, TextField, IconButton, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Tooltip,
  Drawer,
} from "@mui/material";
import {
  Add, Edit, Delete, Business, Search, People, DoubleArrow,
} from "@mui/icons-material";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  childBoxStyleSpaceBetween,
  doubleArrowStyle,
  drawerStyle,
  parentBoxStyle,
  themes,
} from "services/constants";
import CompanyFormPage from "./CompanyFormPage";

function stringToColor(str = "") {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash) % 360;
  return `hsl(${h}, 50%, 45%)`;
}

const companyDrawerStyle = {
  ...drawerStyle,
  "& .MuiDrawer-paper": {
    ...drawerStyle["& .MuiDrawer-paper"],
    width: { xs: "100vw", sm: "760px" },
    maxWidth: "100vw",
  },
};

export default function Companies() {
  const history = useHistory();
  const { domain } = useSelector((s) => s.tokenReducer || {});

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [companyDrawer, setCompanyDrawer] = useState({
    open: false,
    mode: "create",
    companyId: null,
  });

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const res = await customGetAPI(`${domain}crm/companies/${params}`);
      setCompanies(res.responseDetails?.results ?? res.responseDetails ?? []);
    } catch {
      toast.error("Failed to load accounts");
    } finally {
      setLoading(false);
    }
  }, [domain, search]);

  useEffect(() => {
    const t = setTimeout(fetchCompanies, 300);
    return () => clearTimeout(t);
  }, [fetchCompanies]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await customAPI(`${domain}crm/companies/${deleteTarget.id}/`, "DELETE");
      toast.success("Account deleted");
      setDeleteTarget(null);
      fetchCompanies();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const openCreateCompanyDrawer = () => {
    setCompanyDrawer({ open: true, mode: "create", companyId: null });
  };

  const openEditCompanyDrawer = (companyId) => {
    setCompanyDrawer({ open: true, mode: "edit", companyId });
  };

  const closeCompanyDrawer = () => {
    setCompanyDrawer((prev) => ({ ...prev, open: false }));
  };

  const handleCompanySaved = async () => {
    await fetchCompanies();
    closeCompanyDrawer();
  };

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <Business color="primary" />
          <Typography variant="h5" fontWeight={700}>Accounts</Typography>
          <Chip label={companies.length} size="small" color="primary" sx={{ ml: 1 }} />
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreateCompanyDrawer}
        >
          Add Account
        </Button>
      </Box>

      {/* Search */}
      <Box mb={2}>
        <TextField
          size="small"
          placeholder="Search accounts…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} /> }}
          sx={{ width: 320 }}
        />
      </Box>

      {/* Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      ) : companies.length === 0 ? (
        <Box textAlign="center" mt={8}>
          <Business sx={{ fontSize: 64, color: "text.disabled" }} />
          <Typography color="text.secondary" mt={1}>No accounts yet. Add your first one.</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.50" }}>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Sector</strong></TableCell>
                <TableCell><strong>Contacts</strong></TableCell>
                <TableCell><strong>Phone</strong></TableCell>
                <TableCell><strong>Website</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.map((c) => (
                <TableRow
                  key={c.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => history.push(`/admin/crm/companies/${c.id}`)}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar sx={{ bgcolor: stringToColor(c.name), width: 36, height: 36, fontSize: 14 }}>
                        {c.name?.[0]?.toUpperCase()}
                      </Avatar>
                      <Typography fontWeight={600}>{c.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{c.industry || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      icon={<People fontSize="small" />}
                      label={c.contact_count ?? 0}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{c.phone || "—"}</TableCell>
                  <TableCell>
                    {c.website ? (
                      <a href={c.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                        {c.website.replace(/^https?:\/\//, "").slice(0, 30)}
                      </a>
                    ) : "—"}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEditCompanyDrawer(c.id)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(c)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteTarget?.name}</strong>? Linked contacts will keep their data but lose the account link.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={companyDrawer.open}
        onClose={closeCompanyDrawer}
        sx={companyDrawerStyle}
      >
        <Box sx={parentBoxStyle}>
          <Paper
            elevation={0}
            sx={{
              borderBottom: `1px solid ${themes?.miliedLightGray || "#e0e0e0"}`,
              borderRadius: 0,
            }}
          >
            <Box sx={childBoxStyleSpaceBetween}>
              <IconButton onClick={closeCompanyDrawer} sx={doubleArrowStyle}>
                <DoubleArrow />
              </IconButton>
            </Box>
            <Box sx={{ ml: 2.2, mr: 2, mb: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {companyDrawer.mode === "edit" ? "Edit Account" : "Add Account"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {companyDrawer.mode === "edit"
                  ? "Update account details, contact channels, and address."
                  : "Create an account profile with business, contact, and address details."}
              </Typography>
            </Box>
          </Paper>
          <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
            <CompanyFormPage
              key={`${companyDrawer.mode}-${companyDrawer.companyId || "new"}`}
              companyId={companyDrawer.companyId}
              drawerMode
              hideHeader
              onClose={closeCompanyDrawer}
              onSaved={handleCompanySaved}
            />
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}
